// 图片上传服务
// 处理钉钉媒体文件的上传和转换
import { SessionManager } from './aiChatService';

export interface UploadResult {
    success: boolean;
    imageUrl?: string;
    imageBase64?: string;
    error?: string;
}

export interface ImageInfo {
    mediaId: string;
    imageUrl?: string;
    imageBase64?: string;
    filename?: string;
    size?: number;
    mimeType?: string;
}

class ImageUploadService {
    private readonly GENERIC_UPLOAD_ENDPOINT = process.env.REACT_APP_IMAGE_UPLOAD_ENDPOINT || '/api/files/upload';

    // 移除钉钉媒体上传相关方法

    /**
     * 直接上传Base64图片数据
     * @param base64Data Base64编码的图片数据
     * @param filename 文件名
     * @returns 上传结果
     */
    async uploadBase64Image(base64Data: string, filename: string = 'image.jpg', sessionId?: string): Promise<UploadResult> {
        try {
            console.log('开始上传Base64图片数据...');

            // 准备请求体
            const requestBody: any = {
                imageData: base64Data,
                filename: filename
            };

            // 添加sessionId
            const currentSessionId = sessionId || SessionManager.getCurrentSessionId();
            if (currentSessionId) {
                requestBody.sessionId = currentSessionId;
                console.log('🖼️ Base64图片上传包含sessionId:', currentSessionId);
            }

            const response = await fetch(`${this.GENERIC_UPLOAD_ENDPOINT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`上传失败: ${response.status} - ${errorText}`);
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || '上传失败');
            }

            console.log('Base64图片上传成功:', result);

            return {
                success: true,
                imageUrl: result.imageUrl
            };

        } catch (error) {
            console.error('Base64图片上传失败:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : '上传失败'
            };
        }
    }

    /**
     * 通过 multipart/form-data 直接上传文件到后端
     * 优先使用该方式，避免依赖钉钉 mediaId
     */
    async uploadFile(file: File, sessionId?: string): Promise<UploadResult> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            // 添加sessionId
            const currentSessionId = sessionId || SessionManager.getCurrentSessionId();
            if (currentSessionId) {
                formData.append('sessionId', currentSessionId);
                console.log('🖼️ 图片文件上传包含sessionId:', currentSessionId);
            }

            const response = await fetch(this.GENERIC_UPLOAD_ENDPOINT, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`上传失败: ${response.status} - ${errorText}`);
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || '上传失败');
            }

            return {
                success: true,
                imageUrl: result.imageUrl || result.fileUrl || result.url || result.data?.url
            };
        } catch (error) {
            console.error('文件上传失败:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : '上传失败'
            };
        }
    }

    /**
     * 压缩图片（前端压缩）
     * @param file 文件对象
     * @param quality 压缩质量 (0-1)
     * @param maxWidth 最大宽度
     * @param maxHeight 最大高度
     * @returns Base64编码的压缩图片
     */
    async compressImage(
        file: File,
        quality: number = 0.8,
        maxWidth: number = 1920,
        maxHeight: number = 1080
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // 计算压缩后的尺寸
                let { width, height } = this.calculateCompressedSize(
                    img.width,
                    img.height,
                    maxWidth,
                    maxHeight
                );

                canvas.width = width;
                canvas.height = height;

                // 绘制压缩后的图片
                ctx?.drawImage(img, 0, 0, width, height);

                // 转换为Base64
                const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedBase64);
            };

            img.onerror = () => {
                reject(new Error('图片加载失败'));
            };

            // 创建图片URL
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 计算压缩后的尺寸
     */
    private calculateCompressedSize(
        originalWidth: number,
        originalHeight: number,
        maxWidth: number,
        maxHeight: number
    ): { width: number; height: number } {
        let width = originalWidth;
        let height = originalHeight;

        // 按比例缩放
        if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
        }

        if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
        }

        return { width: Math.round(width), height: Math.round(height) };
    }

    /**
     * 验证图片文件类型
     */
    isValidImageType(file: File): boolean {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        return validTypes.includes(file.type);
    }

    /**
     * 验证图片文件大小
     */
    isValidImageSize(file: File, maxSizeMB: number = 10): boolean {
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        return file.size <= maxSizeBytes;
    }

    /**
     * 生成图片缩略图
     */
    async generateThumbnail(
        imageData: string,
        thumbnailSize: number = 150
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = thumbnailSize;
                canvas.height = thumbnailSize;

                // 计算裁剪区域（居中裁剪）
                const size = Math.min(img.width, img.height);
                const x = (img.width - size) / 2;
                const y = (img.height - size) / 2;

                // 绘制缩略图
                ctx?.drawImage(
                    img,
                    x, y, size, size,
                    0, 0, thumbnailSize, thumbnailSize
                );

                const thumbnailBase64 = canvas.toDataURL('image/jpeg', 0.7);
                resolve(thumbnailBase64);
            };

            img.onerror = () => {
                reject(new Error('缩略图生成失败'));
            };

            img.src = imageData;
        });
    }
}

// 导出单例实例
export const imageUploadService = new ImageUploadService();
export default ImageUploadService;
